import { useMemo } from 'react';
import { Exam, Clock, Phone, CalendarBlank, WarningCircle } from '@phosphor-icons/react';

const WEEKDAYS = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];
const MONTHS = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

function parseExamDate(s) {
    if (!s) return null;
    // Accept "YYYY-MM-DD"
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
}

function formatDay(d) {
    return String(d.getDate()).padStart(2, '0');
}
function formatMonthName(d) {
    return MONTHS[d.getMonth()];
}
function formatYear(d) {
    return d.getFullYear();
}
function weekdayName(d) {
    return WEEKDAYS[d.getDay()];
}

function daysUntil(date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

function urgencyClass(days) {
    if (days < 0) return 'bg-gray-500/15 text-gray-300 border-gray-400/30';
    if (days <= 3) return 'bg-red-500/15 text-red-300 border-red-400/40';
    if (days <= 7) return 'bg-amber-500/15 text-amber-300 border-amber-400/40';
    if (days <= 14) return 'bg-[#D4A537]/15 text-[#E5B84A] border-[#D4A537]/40';
    return 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30';
}

function urgencyLabel(days) {
    if (days < 0) return `${Math.abs(days)} ${Math.abs(days) === 1 ? 'dag geleden' : 'dagen geleden'}`;
    if (days === 0) return 'VANDAAG';
    if (days === 1) return 'MORGEN';
    return `Over ${days} dagen`;
}

export default function ExamSchedule({ students = [], onSelectStudent }) {
    const withExams = useMemo(() => {
        const rows = (students || [])
            .filter((s) => !s.external)
            .map((s) => ({ ...s, _examDate: parseExamDate(s.exam_date) }))
            .filter((s) => s._examDate)
            .sort((a, b) => a._examDate - b._examDate);
        return rows;
    }, [students]);

    if (!withExams.length) {
        return (
            <div className="bg-[#2D3748] rounded-lg border border-[#4A5568] p-8 text-center" data-testid="exam-empty-state">
                <Exam size={36} weight="duotone" className="mx-auto text-[#D4A537] mb-3" />
                <h3 className="text-lg font-semibold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Geen examens ingepland
                </h3>
                <p className="text-sm text-gray-400 max-w-md mx-auto">
                    Voeg via "Wijzig gegevens" bij een leerling een examen-datum toe en die verschijnt automatisch hier in de agenda.
                </p>
            </div>
        );
    }

    return (
        <div className="bg-[#2D3748] rounded-lg border border-[#4A5568]" data-testid="exam-schedule">
            <div className="p-6 border-b border-[#4A5568] flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-xl font-semibold text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <Exam size={22} weight="fill" className="text-[#D4A537]" />
                        Examenagenda
                    </h2>
                    <p className="text-sm text-gray-400 mt-1">
                        Alle leerlingen met een geplande examendatum, gesorteerd op datum.
                    </p>
                </div>
                <div className="text-xs text-gray-500 px-3 py-1.5 rounded-full bg-[#1A202C] border border-[#4A5568]">
                    {withExams.length} {withExams.length === 1 ? 'examen' : 'examens'}
                </div>
            </div>

            <ul className="divide-y divide-[#4A5568]">
                {withExams.map((s) => {
                    const d = s._examDate;
                    const days = daysUntil(d);
                    const time = s.exam_time || null;
                    return (
                        <li
                            key={s.id}
                            className="p-4 sm:p-5 hover:bg-[#3D4A5C]/50 transition-colors cursor-pointer flex items-start gap-3 sm:gap-4"
                            onClick={() => onSelectStudent?.(s)}
                            data-testid={`exam-row-${s.id}`}
                        >
                            {/* Date block */}
                            <div className={`flex-shrink-0 rounded-lg border ${urgencyClass(days)} p-3 w-20 text-center`}>
                                <div className="text-[10px] uppercase tracking-wider opacity-80">
                                    {weekdayName(d).slice(0, 3)}
                                </div>
                                <div className="text-2xl font-bold leading-tight">{formatDay(d)}</div>
                                <div className="text-[10px] capitalize opacity-90">{formatMonthName(d).slice(0, 3)}</div>
                                <div className="text-[10px] opacity-70 mt-0.5">{formatYear(d)}</div>
                            </div>

                            {/* Details */}
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-baseline gap-2">
                                    <span className="text-base sm:text-lg font-semibold text-white truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {s.name}
                                    </span>
                                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${urgencyClass(days)}`}>
                                        {urgencyLabel(days)}
                                    </span>
                                </div>

                                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-300">
                                    <span className="inline-flex items-center gap-1.5">
                                        <CalendarBlank size={13} className="text-gray-400" />
                                        <span className="capitalize">{weekdayName(d)} {formatDay(d)} {formatMonthName(d)} {formatYear(d)}</span>
                                    </span>
                                    {time && (
                                        <span className="inline-flex items-center gap-1.5">
                                            <Clock size={13} className="text-gray-400" />
                                            {time}
                                        </span>
                                    )}
                                    {!time && (
                                        <span className="inline-flex items-center gap-1.5 text-amber-300">
                                            <WarningCircle size={13} />
                                            Geen tijd ingesteld
                                        </span>
                                    )}
                                    {s.exam_table_number && (
                                        <span
                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#D4A537]/40 bg-[#D4A537]/10 text-[#E5B84A] text-[11px] font-semibold uppercase tracking-wider"
                                            data-testid={`exam-table-${s.id}`}
                                        >
                                            Tafel {s.exam_table_number}
                                        </span>
                                    )}
                                    {s.phone && (
                                        <a
                                            href={`tel:${s.phone}`}
                                            onClick={(e) => e.stopPropagation()}
                                            className="inline-flex items-center gap-1.5 text-gray-400 hover:text-[#D4A537] transition-colors"
                                            data-testid={`exam-call-${s.id}`}
                                        >
                                            <Phone size={13} />
                                            {s.phone}
                                        </a>
                                    )}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
